import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import getTheme from '../theme';

const STORAGE_KEY = 'themePreference';

const ThemeModeContext = createContext({
    preference: 'system',
    resolvedMode: 'light',
    setPreference: () => {},
    toggleMode: () => {},
});

const readStoredPreference = () => {
    try {
        const value = localStorage.getItem(STORAGE_KEY);
        return value === 'light' || value === 'dark' ? value : 'system';
    } catch (e) {
        return 'system';
    }
};

const getSystemDark = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

export function AppThemeProvider({ children }) {
    const [preference, setPreferenceState] = useState(readStoredPreference);
    const [systemDark, setSystemDark] = useState(getSystemDark);

    const resolvedMode =
        preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (event) => setSystemDark(event.matches);
        if (mql.addEventListener) {
            mql.addEventListener('change', handleChange);
            return () => mql.removeEventListener('change', handleChange);
        }
        // Safari < 14
        mql.addListener(handleChange);
        return () => mql.removeListener(handleChange);
    }, []);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', resolvedMode);
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) {
            meta.setAttribute(
                'content',
                resolvedMode === 'dark' ? '#101418' : '#f9f9f9'
            );
        }
    }, [resolvedMode]);

    const setPreference = useCallback((value) => {
        const next =
            value === 'light' || value === 'dark' ? value : 'system';
        setPreferenceState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch (e) {
            // localStorage unavailable (e.g. private browsing) — theme still
            // works for the session, it just won't persist.
        }
    }, []);

    const toggleMode = useCallback(() => {
        setPreference(resolvedMode === 'dark' ? 'light' : 'dark');
    }, [resolvedMode, setPreference]);

    const contextValue = useMemo(
        () => ({ preference, resolvedMode, setPreference, toggleMode }),
        [preference, resolvedMode, setPreference, toggleMode]
    );

    const theme = useMemo(() => getTheme(resolvedMode), [resolvedMode]);

    return (
        <ThemeModeContext.Provider value={contextValue}>
            <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
        </ThemeModeContext.Provider>
    );
}

export const useThemeMode = () => useContext(ThemeModeContext);
